import {
  CreditCardBorrowerDocumentationRequired,
  DocumentationRequired,
  DocumentationRequiredType,
  getAllDocumentationRequiredByLoanId,
  patchAddRequiredFileTypeToDocumentationRequired,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { useRequest } from '@/hooks/requests/useRequest';

interface AddRequiredFileToDocumentationRequired {
  loanId: string;
  fileTypeId: string;
  documentationRequired: DocumentationRequired | CreditCardBorrowerDocumentationRequired;
  documentationRequiredType: DocumentationRequiredType;
}

export const useAddRequiredFile = () => {
  const { request } = useRequest();

  const [addRequiredFileLoading, setAddRequiredFileLoading] = useState(false);
  const [addRequiredFiles, setAddRequiredFiles] = useState(false);

  const resetAddRequiredFileParams = () => {
    setAddRequiredFileLoading(false);
    setAddRequiredFiles(false);
  };

  const addRequiredFileToDocumentationRequired = async ({
    loanId,
    fileTypeId,
    documentationRequired,
    documentationRequiredType,
  }: AddRequiredFileToDocumentationRequired) => {
    try {
      setAddRequiredFileLoading(true);
      await request(patchAddRequiredFileTypeToDocumentationRequired, {
        id: documentationRequired.id,
        fileTypeId,
        documentationRequiredType,
      });
      await waitUntilRequiredFileIsAdded({
        loanId,
        fileTypeId,
        documentationRequiredType,
        documentationRequired,
      });
    } catch (error) {
    } finally {
      setAddRequiredFileLoading(false);
    }
  };

  const waitUntilRequiredFileIsAdded = async ({
    loanId,
    fileTypeId,
    documentationRequiredType,
    documentationRequired,
  }: AddRequiredFileToDocumentationRequired): Promise<void> => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      (async function waitGetAllDocumentationRequired() {
        attempts += 1;
        if (attempts > 5) return reject();
        const allDocumentationRequired = await getAllDocumentationRequiredByLoanId({ loanId });
        let currentDocumentationRequireds: DocumentationRequired[] | undefined;
        if (documentationRequiredType === DocumentationRequiredType.LOAN && allDocumentationRequired.loan)
          currentDocumentationRequireds = [allDocumentationRequired.loan];
        if (documentationRequiredType === DocumentationRequiredType.BORROWER)
          currentDocumentationRequireds = allDocumentationRequired.borrowers;
        const currentDocumentationRequired = currentDocumentationRequireds?.find(
          (currentDocumentationRequired) => currentDocumentationRequired.id === documentationRequired.id,
        );
        if (currentDocumentationRequired?.requiredFiles?.some((requiredFile) => requiredFile.id === fileTypeId)) {
          return resolve();
        }
        setTimeout(await waitGetAllDocumentationRequired, 1000);
      })();
    });
  };

  return {
    addRequiredFileToDocumentationRequired,
    addRequiredFileLoading,
    addRequiredFiles,
    setAddRequiredFiles,
    resetAddRequiredFileParams,
  };
};
